import { Avatar, Box, Card, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import type { KnownNotificationResource } from '../../../api/notifications'
import { IconProjectPlaceholder } from '../../../icons/IconProjectPlaceholder'
import { NotificationEntityHeader } from './NotificationEntityHeader'
import { mediaSourceUrl } from './utils'
import { createNotificationNavHandlers, getActorPathKey, getProjectSlug } from './notificationNav'
import { ActorNameSpan, ProjectNameQuoted } from './notificationTypography'
import { notificationBodyWrapSx } from './notificationTextClamp'

type Props = {
  notification: Extract<KnownNotificationResource, { type: 'project.subscription.deleted' }>
}

export function ProjectSubscriptionDeletedNotificationCard({ notification }: Props) {
  const navigate = useNavigate()
  const { onProjectClick, onUserClick } = createNotificationNavHandlers(navigate)
  const payload: any = (notification as any).payload ?? {}
  const project = payload.project ?? {}
  const actor = payload.actor ?? {}

  const projectTitle = typeof project.title === 'string' && project.title.trim().length > 0 ? project.title : 'проект'
  const actorName = typeof actor.fullname === 'string' && actor.fullname.trim().length > 0 ? actor.fullname : 'Пользователь'
  const projectAvatarSrc = mediaSourceUrl(project.image_url) ?? undefined
  const projectSlug = getProjectSlug(project)
  const actorPathKey = getActorPathKey(actor, notification.actor_user_id)
  const hasActor = Boolean(payload?.actor)

  return (
    <Card variant="primaryAdaptiveCard" sx={{ p: 2 }}>
      <Stack direction="column" minWidth={0} flex={1} gap={1.25}>
        <Box
          onClick={(e) => onProjectClick(e, projectSlug)}
          sx={{ cursor: projectSlug ? 'pointer' : 'default', alignSelf: 'stretch' }}
        >
          <NotificationEntityHeader
            title={projectTitle}
            avatarSrc={projectAvatarSrc}
            fallbackIcon={<IconProjectPlaceholder fontSize="small" sx={{ color: 'inherit' }} />}
            subtitle="Отписка от проекта"
          />
        </Box>

        <Stack direction="row" gap={1.5} alignItems="center" minWidth={0}>
          {hasActor && (
            <Box
              onClick={actorPathKey ? (e) => onUserClick(e, actorPathKey) : undefined}
              sx={{ cursor: actorPathKey ? 'pointer' : 'default', flexShrink: 0 }}
            >
              <Avatar src={mediaSourceUrl(actor.avatar_url) ?? undefined} alt={actorName} sx={{ width: 34, height: 34 }} />
            </Box>
          )}
          <Typography variant="black-14-normal" sx={notificationBodyWrapSx}>
            {hasActor ? (
              <>
                <ActorNameSpan pathKey={actorPathKey} onUserClick={onUserClick}>
                  {actorName}
                </ActorNameSpan>
                {' отписался(ась) от '}
                <ProjectNameQuoted name={projectTitle} pathKey={projectSlug} onNameClick={onProjectClick} />
              </>
            ) : (
              <>
                Подписка на <ProjectNameQuoted name={projectTitle} pathKey={projectSlug} onNameClick={onProjectClick} /> удалена
              </>
            )}
          </Typography>
        </Stack>

        <Typography variant="black-12-light" noWrap sx={{ opacity: 0.8 }}>
          {notification.read_at ? 'Прочитано' : 'Непрочитано'} • {new Date(notification.created_at).toLocaleString()}
        </Typography>
      </Stack>
    </Card>
  )
}
